import React, { useState } from 'react';
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
  useCreateData,
  useDeleteData,
  useFetchData,
  useUpdateData,
} from '../../../hooks';
import { useAuthStore } from '../../../store';

// Admin / Configuration — Internal Reference (IR) values: module/code/description/value.
interface IrValue {
  id?: string;
  module?: string;
  code: string;
  description?: string;
  value?: string;
}

const API = '/ir-values';

const IrValuesPage = () => {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'ROLE_ADMIN';
  const { data, isLoading } = useFetchData<{ data: IrValue[] }>(API, {
    length: 999999,
  });
  const create = useCreateData<IrValue>(API);
  const update = useUpdateData<IrValue>(API);
  const del = useDeleteData<IrValue>(API);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<IrValue | null>(null);
  const [form] = Form.useForm();

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setOpen(true);
  };
  const openEdit = (rec: IrValue) => {
    setEditing(rec);
    form.setFieldsValue(rec);
    setOpen(true);
  };

  const onSubmit = async () => {
    const values = await form.validateFields();
    const onSuccess = () => {
      message.success(editing ? 'Value updated' : 'Value created');
      setOpen(false);
    };
    const onError = (e: any) =>
      message.error(e?.response?.data?.message ?? 'Save failed');
    if (editing?.id) {
      update.mutate(
        { id: editing.id, form: { ...editing, ...values } },
        { onSuccess, onError },
      );
    } else {
      create.mutate(values, { onSuccess, onError });
    }
  };

  const columns: any[] = [
    { title: 'Module', dataIndex: 'module' },
    { title: 'Code', dataIndex: 'code' },
    { title: 'Description', dataIndex: 'description' },
    { title: 'Value', dataIndex: 'value' },
  ];
  if (isAdmin) {
    columns.push({
      title: 'Actions',
      width: 160,
      render: (_: unknown, rec: IrValue) => (
        <Space>
          <Button size={'small'} onClick={() => openEdit(rec)}>
            Edit
          </Button>
          <Popconfirm
            title={'Delete this value?'}
            onConfirm={() =>
              del.mutate(
                { id: rec.id! },
                { onSuccess: () => message.success('Value deleted') },
              )
            }
          >
            <Button size={'small'} danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    });
  }

  return (
    <div className={'page-wrapper'}>
      <div className={'flex items-center justify-between mb-3'}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          IR Values
        </Typography.Title>
        {isAdmin && (
          <Button type={'primary'} icon={<PlusOutlined />} onClick={openCreate}>
            New Value
          </Button>
        )}
      </div>
      <Table
        rowKey={(r) => r.id || r.code}
        loading={isLoading}
        dataSource={data?.data || []}
        columns={columns}
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />
      <Modal
        title={editing ? 'Edit Value' : 'New Value'}
        open={open}
        onOk={onSubmit}
        onCancel={() => setOpen(false)}
        confirmLoading={create.isPending || update.isPending}
        destroyOnClose
      >
        <Form form={form} layout={'vertical'}>
          <Form.Item name={'module'} label={'Module'}>
            <Input />
          </Form.Item>
          <Form.Item
            name={'code'}
            label={'Code'}
            rules={[{ required: true, message: 'Code is required' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name={'description'} label={'Description'}>
            <Input />
          </Form.Item>
          <Form.Item name={'value'} label={'Value'}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default IrValuesPage;
